/**
 * Owner email verification — `/owners/verify`, the 6-digit step from the
 * free-tier acquisition prototype.
 *
 * ⚠️ DEPENDS ON SUPABASE AUTH CONFIG. A code only reaches the owner
 * if the "Confirm signup" email template renders `{{ .Token }}`. Out of the
 * box Supabase sends `{{ .ConfirmationURL }}` — a link — and no code exists to
 * type. That is why this page sits behind `VITE_OWNER_SIGNUP_OTP`, default
 * off: with the flag off the signup page keeps its "check your email" state
 * and the link keeps working, so turning this on is a deliberate act taken
 * together with the template change.
 *
 * The link path is NOT replaced. Even with the code on, the same email can
 * carry both, and an owner who clicks the link still lands on
 * `/owners/confirmed`. This page is an alternative, not a gate.
 *
 * Laid out against the prototype's verify screen: a 400px card on #FAF8F4,
 * 54px code boxes, a pill action. Centred in the space below the fixed 72px
 * site nav rather than sitting at the top of it.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useLanguage } from "@/contexts/LanguageContext";
import { Button } from "@/components/ui/button";
import { getSiteUrlForPath } from "@/lib/site-url";

// Must match the project's `mailer_otp_length`. Both live and dev are set to
// 8, not supabase-js's 6 default — a 6-box form cannot accept the code it is
// emailed. If the project setting is ever changed, this and the `{{ .Token }}`
// mail template move together.
const CODE_LENGTH = 8;
/** Supabase rate-limits resends; the prototype counts down from 30. */
const RESEND_SECONDS = 30;

export default function OwnerVerify() {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const email = useMemo(
    () => (searchParams.get("email") ?? "").trim(),
    [searchParams]
  );

  const [digits, setDigits] = useState<string[]>(Array(CODE_LENGTH).fill(""));
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [resentAt, setResentAt] = useState<number | null>(null);
  const [resent, setResent] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(0);
  const inputs = useRef<(HTMLInputElement | null)[]>([]);

  const code = digits.join("");
  const complete = code.length === CODE_LENGTH && !digits.includes("");

  // Countdown for the resend link.
  useEffect(() => {
    if (resentAt === null) return;
    const tick = () => {
      const elapsed = Math.floor((Date.now() - resentAt) / 1000);
      setSecondsLeft(Math.max(0, RESEND_SECONDS - elapsed));
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [resentAt]);

  const focusInput = (i: number) => inputs.current[i]?.focus();

  /** Fill forward from `index` — used by a paste, which spans several boxes. */
  function fillFrom(index: number, clean: string) {
    setDigits((d) => {
      const next = [...d];
      for (let i = 0; i < clean.length && index + i < CODE_LENGTH; i++) {
        next[index + i] = clean[i];
      }
      return next;
    });
    focusInput(Math.min(index + clean.length - 1, CODE_LENGTH - 1));
  }

  function handlePaste(index: number, e: React.ClipboardEvent) {
    // Keep digits only: a pasted "1 2 3" or a stray letter must not land in a
    // box and silently make the code wrong.
    const clean = e.clipboardData.getData("text").replace(/\D/g, "");
    if (!clean) return;
    e.preventDefault();
    fillFrom(index, clean);
  }

  function setDigit(index: number, value: string) {
    const clean = value.replace(/\D/g, "");
    if (!clean) {
      setDigits((d) => d.map((x, i) => (i === index ? "" : x)));
      return;
    }
    // One box holds one digit, but three different things arrive here:
    //
    //   "4"         typing into an empty box
    //   "94"        a correction over a FILLED box — the browser hands back
    //               what was there plus the keystroke
    //   "12345678"  the browser autofilling the whole code into the first box
    //               from the one-time-code hint. This arrives as a CHANGE, not
    //               a paste, so handlePaste never sees it.
    //
    // Length tells them apart: a correction is exactly two characters, one of
    // which is the digit already in this box. Anything longer is a code and
    // fills forward — taking only its last character would throw seven digits
    // away and leave the form permanently disabled.
    //
    // WHICH side carries the new digit depends on where the caret sat. After
    // the old digit (the common case, and what a click on a filled box gives)
    // the browser reports old+new; before it — Home, arrow-left, a click on
    // the box's left half — it reports new+old. These boxes set no maxLength,
    // so both genuinely reach here. Reading only the old+new shape would send
    // a caret-at-start correction down the fill-forward path and overwrite the
    // NEXT box, which was holding a correct digit.
    const previous = digits[index];
    const isCorrection =
      clean.length === 2 &&
      !!previous &&
      (clean[0] === previous || clean[1] === previous);

    if (!isCorrection && clean.length > 1) {
      fillFrom(index, clean);
      return;
    }

    // Whichever character is NOT the digit already there is the new one. When
    // both are (retyping the same digit) either answers the same.
    const digit = !isCorrection
      ? clean[0]
      : clean[0] === previous
        ? clean[1]
        : clean[0];
    setDigits((d) => d.map((x, i) => (i === index ? digit : x)));
    if (index < CODE_LENGTH - 1) focusInput(index + 1);
  }

  function handleKeyDown(index: number, e: React.KeyboardEvent) {
    if (e.key === "Backspace" && !digits[index] && index > 0) {
      // Backspace on an empty box steps back, which is what every OTP field
      // does and what fingers expect.
      e.preventDefault();
      focusInput(index - 1);
    }
    if (e.key === "ArrowLeft" && index > 0) focusInput(index - 1);
    if (e.key === "ArrowRight" && index < CODE_LENGTH - 1)
      focusInput(index + 1);
  }

  const verify = useCallback(async () => {
    if (!complete || submitting) return;
    setResent(false);
    setError(null);
    setSubmitting(true);
    try {
      const { error: verifyError } = await supabase.auth.verifyOtp({
        email,
        token: code,
        type: "signup",
      });
      if (verifyError) {
        setError(
          /expired/i.test(verifyError.message)
            ? t(
                "ownerVerify.errorExpired",
                "Der Code ist abgelaufen. Bitte fordern Sie einen neuen an."
              )
            : t(
                "ownerVerify.errorInvalid",
                "Code nicht korrekt. Bitte prüfen Sie den Code."
              )
        );
        return;
      }
      // Verified and signed in — the portal is the point of the whole flow.
      navigate("/admin/properties", { replace: true });
    } finally {
      setSubmitting(false);
    }
  }, [complete, submitting, email, code, navigate, t]);

  async function resend() {
    if (secondsLeft > 0) return;
    setError(null);
    const { error: resendError } = await supabase.auth.resend({
      type: "signup",
      email,
      // Without this the resent link lands on the site root, so the second
      // mail would behave differently from the first. getSiteUrlForPath, not
      // window.location.origin: AuthContext.signUp builds the FIRST mail's link
      // with it, and where VITE_SITE_URL differs from the current origin —
      // localhost, a preview deploy — the origin would send the resent link
      // somewhere the allowlist does not cover.
      options: {
        emailRedirectTo: getSiteUrlForPath("/owners/confirmed"),
      },
    });
    if (resendError) {
      setError(resendError.message);
      return;
    }
    setResent(true);
    setResentAt(Date.now());
  }

  // No email in the URL means the page was opened directly; there is nothing
  // to verify against, so send them back rather than showing a dead form.
  if (!email) {
    return (
      <Shell>
        <div className="w-full rounded-[20px] border border-[#ECE7DD] bg-white px-7 pb-7 pt-[26px] text-center shadow-[0_12px_32px_rgba(15,76,74,0.06)]">
          <h1 className="text-2xl font-semibold tracking-[-0.03em] text-[#0F4C4A]">
            {t("ownerVerify.noEmailTitle", "Link unvollständig")}
          </h1>
          <p className="mt-2 text-sm text-[#47605E]">
            {t(
              "ownerVerify.noEmailBody",
              "Bitte starten Sie die Registrierung erneut."
            )}
          </p>
          <Button
            onClick={() => navigate("/owners/signup")}
            className="mt-5 h-[46px] rounded-full bg-[#0F4C4A] px-6 text-[14.5px] font-semibold text-white hover:bg-[#0B3A38]"
          >
            {t("ownerVerify.backToSignup", "Zur Registrierung")}
          </Button>
        </div>
      </Shell>
    );
  }

  return (
    <Shell>
      <div className="w-full rounded-[20px] border border-[#ECE7DD] bg-white px-7 pb-7 pt-[26px] shadow-[0_12px_32px_rgba(15,76,74,0.06)]">
        <h1 className="text-2xl font-semibold tracking-[-0.03em] text-[#0F4C4A]">
          {t("ownerVerify.title", "E-Mail bestätigen")}
        </h1>
        <p className="mt-2 text-[13.5px] leading-[1.5] text-[#47605E]">
          {t("ownerVerify.body", "Wir haben einen 8-stelligen Code an")} {email}{" "}
          {t("ownerVerify.bodyEnd", "geschickt.")}
        </p>

        {/* CODE_LENGTH boxes. Literal classes — Tailwind cannot read the
            constant — so these move with it. */}
        <div className="mt-6 grid grid-cols-4 gap-2 sm:grid-cols-8">
          {digits.map((d, i) => (
            <input
              key={i}
              ref={(el) => (inputs.current[i] = el)}
              value={d}
              onChange={(e) => setDigit(i, e.target.value)}
              onPaste={(e) => handlePaste(i, e)}
              onKeyDown={(e) => handleKeyDown(i, e)}
              inputMode="numeric"
              autoComplete={i === 0 ? "one-time-code" : "off"}
              aria-label={t("ownerVerify.digitLabel", "Ziffer") + ` ${i + 1}`}
              className="h-[54px] w-full rounded-xl border border-[#ECE7DD] bg-white text-center text-[22px] font-semibold tabular-nums text-[#0F4C4A] outline-none focus:border-[#2FB3AA]"
            />
          ))}
        </div>

        {error && (
          <p role="alert" className="mt-2.5 text-xs text-[#C0714F]">
            {error}
          </p>
        )}

        {/* The prototype confirms the resend in words, not only by starting a
            countdown — without it a second tap feels like nothing happened. */}
        {resent && !error && (
          <p className="mt-2.5 text-xs text-[#0F4C4A]">
            {t("ownerVerify.resent", "Code erneut gesendet.")}
          </p>
        )}

        <div className="mt-[18px] flex items-center justify-between gap-3">
          <button
            type="button"
            onClick={resend}
            disabled={secondsLeft > 0}
            className="text-[13px] font-medium text-[#2FB3AA] hover:text-[#0F4C4A] disabled:cursor-default disabled:text-[#7A8E8C]"
          >
            {secondsLeft > 0
              ? t("ownerVerify.resendIn", "Erneut senden in") +
                ` ${secondsLeft}s`
              : t("ownerVerify.resend", "Code erneut senden")}
          </button>
          <button
            type="button"
            onClick={() => navigate("/owners/signup")}
            className="text-[13px] text-[#7A8E8C] hover:text-[#0F4C4A]"
          >
            {t("ownerVerify.changeEmail", "E-Mail ändern")}
          </button>
        </div>

        <Button
          onClick={verify}
          disabled={!complete || submitting}
          className="mt-[22px] h-[46px] w-full rounded-full bg-[#0F4C4A] text-[14.5px] font-semibold text-white hover:bg-[#0B3A38] disabled:opacity-50"
        >
          {submitting
            ? t("ownerVerify.verifying", "Wird geprüft …")
            : t("ownerVerify.submit", "Weiter zum Portal")}
        </Button>
      </div>
    </Shell>
  );
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    // The site nav is `fixed` at 72px, so the page owes it that much clearance
    // or the card slides underneath it. Centring is against the space that is
    // LEFT once the nav is accounted for — otherwise a vertically centred card
    // sits visibly low.
    <div className="bg-[#FAF8F4] pt-[72px]">
      <main className="flex min-h-[calc(100vh-72px)] flex-col items-center justify-center px-4 py-7 sm:px-6 sm:py-[52px]">
        <div className="w-full max-w-[400px]">{children}</div>
      </main>
    </div>
  );
}
